import { useEffect, useMemo, useState } from 'react';
import {
  Autocomplete,
  Button,
  CircularProgress,
  IconButton,
  LinearProgress,
  MenuItem,
  TextField,
  Tooltip,
} from '@mui/material';
import {
  MdFilterListOff,
  MdInsights,
  MdRefresh,
} from 'react-icons/md';
import { Page } from '@components/layout/Page';
import { PageEmpty } from '@components/layout/PageEmpty';
import { DataPagination } from '@components/ui/DataPagination';
import { GridSkeleton } from '@components/ui/GridSkeleton';
import { TabFilter } from '@components/ui/TabFilter';
import { ForecastChartDialog } from '@features/forecasts/components/ForecastChartDialog';
import { ForecastEditDialog } from '@features/forecasts/components/ForecastEditDialog';
import { ForecastsGrid } from '@features/forecasts/components/ForecastsGrid';
import { useForecasts } from '@features/forecasts/hooks';
import {
  FORECAST_HORIZONS,
  FORECAST_METRICS,
  type Forecast,
  type ForecastSortKey,
  type ListForecastsParams,
  type SortDirection,
} from '@features/forecasts/schema';
import { useApmcOptions } from '@features/apmcs/hooks';
import { useCropOptions } from '@features/crops/hooks';
import type { CropOption } from '@features/crops/schema';
import { useVarietyOptions } from '@features/varieties/hooks';
import type { VarietyOption } from '@features/varieties/schema';
import type { ApmcOption } from '@features/apmcs/schema';

const DEFAULT_PAGE_SIZE = 50;

type ActiveTabValue = '' | 'true' | 'false';

const ACTIVE_TABS: ReadonlyArray<{ value: ActiveTabValue; label: string }> = [
  { value: '', label: 'All' },
  { value: 'true', label: 'Active' },
  { value: 'false', label: 'Inactive' },
];

export default function ForecastsScreen() {
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);
  const [searchInput, setSearchInput] = useState('');
  const [searchText, setSearchText] = useState('');
  const [activeTab, setActiveTab] = useState<ActiveTabValue>('');
  const [cropId, setCropId] = useState<number | null>(null);
  const [varietyId, setVarietyId] = useState<number | null>(null);
  const [apmcId, setApmcId] = useState<number | null>(null);
  const [metric, setMetric] = useState<'' | 'max' | 'modal'>('');
  const [horizon, setHorizon] = useState<number | ''>('');
  const [sort, setSort] = useState<{
    sortBy?: ForecastSortKey;
    sortDir?: SortDirection;
  }>({});

  const [editingRow, setEditingRow] = useState<Forecast | null>(null);
  const [chartingRow, setChartingRow] = useState<Forecast | null>(null);

  // Debounce search input.
  useEffect(() => {
    const t = setTimeout(() => setSearchText(searchInput.trim()), 250);
    return () => clearTimeout(t);
  }, [searchInput]);

  // Reset to page 1 whenever filters / sort / tab change.
  useEffect(() => {
    setPage(1);
  }, [
    searchText,
    activeTab,
    cropId,
    varietyId,
    apmcId,
    metric,
    horizon,
    pageSize,
    sort,
  ]);

  const params = useMemo<ListForecastsParams>(
    () => ({
      page,
      pageSize,
      searchText: searchText || undefined,
      cropId: cropId ?? undefined,
      varietyId: varietyId ?? undefined,
      apmcId: apmcId ?? undefined,
      metric: metric || undefined,
      horizon: horizon === '' ? undefined : horizon,
      isActive: activeTab || undefined,
      sortBy: sort.sortBy,
      sortDir: sort.sortDir,
    }),
    [
      page,
      pageSize,
      searchText,
      cropId,
      varietyId,
      apmcId,
      metric,
      horizon,
      activeTab,
      sort,
    ],
  );

  const { data, isLoading, isFetching, refetch } = useForecasts(params);
  const { data: crops = [] } = useCropOptions();
  const { data: apmcs = [] } = useApmcOptions();
  const { data: allVarieties = [] } = useVarietyOptions();

  const varietyOptions = useMemo<VarietyOption[]>(
    () =>
      cropId == null
        ? []
        : allVarieties.filter((v) => v.cropId === String(cropId)),
    [allVarieties, cropId],
  );
  const varietyDisabled = cropId == null;

  const cropById = useMemo(
    () => new Map(crops.map((c) => [c.id, c])),
    [crops],
  );
  const apmcById = useMemo(
    () => new Map(apmcs.map((a) => [a.id, a])),
    [apmcs],
  );
  const varietyById = useMemo(
    () => new Map(varietyOptions.map((v) => [v.id, v])),
    [varietyOptions],
  );

  const cropValue: CropOption | null =
    cropId == null ? null : cropById.get(String(cropId)) ?? null;
  const apmcValue: ApmcOption | null =
    apmcId == null ? null : apmcById.get(String(apmcId)) ?? null;
  const varietyValue: VarietyOption | null =
    varietyId == null ? null : varietyById.get(String(varietyId)) ?? null;

  const totalRecords = data?.paginationDetails?.totalRecords ?? 0;
  const hasActiveFilters = Boolean(
    searchText ||
      activeTab ||
      cropId !== null ||
      varietyId !== null ||
      apmcId !== null ||
      metric ||
      horizon !== '',
  );
  const isInitialLoad = isLoading && !data;
  const showEmpty = !isLoading && (data?.rowData?.length ?? 0) === 0;

  const clearFilters = () => {
    setSearchInput('');
    setSearchText('');
    setActiveTab('');
    setCropId(null);
    setVarietyId(null);
    setApmcId(null);
    setMetric('');
    setHorizon('');
  };

  const variety = (
    <Autocomplete
      size="small"
      options={varietyOptions}
      disabled={varietyDisabled}
      value={varietyValue}
      onChange={(_, opt) => setVarietyId(opt ? Number(opt.id) : null)}
      getOptionLabel={(opt) => opt.name}
      isOptionEqualToValue={(opt, v) => opt.id === v.id}
      noOptionsText="No varieties for this crop"
      className="w-full md:w-[180px]"
      // Lock-icon startAdornment dropped — see MarketPriceFiltersBar for the
      // reasoning. Disabled state + Tooltip hover hint communicate the cascade.
      renderInput={(p) => <TextField {...p} label="Variety" />}
    />
  );

  return (
    <Page
      title="Forecasts"
      subtitle="Model-produced price predictions per series, metric, horizon."
      fullHeight
      headerRightSlot={
        <TabFilter
          options={ACTIVE_TABS}
          current={activeTab}
          onChange={setActiveTab}
          ariaLabel="Filter by active state"
        />
      }
    >
      <div className="mb-3 flex flex-col gap-3 rounded-xl bg-surface-muted px-3 py-3 md:flex-row md:flex-wrap md:items-end md:px-4">
        <TextField
          label="Search notes"
          size="small"
          value={searchInput}
          onChange={(e) => setSearchInput(e.target.value)}
          className="w-full md:min-w-[200px] md:flex-1"
        />
        <Autocomplete
          size="small"
          options={crops}
          value={cropValue}
          onChange={(_, opt) => {
            setCropId(opt ? Number(opt.id) : null);
            setVarietyId(null);
          }}
          getOptionLabel={(opt) => opt.name}
          isOptionEqualToValue={(opt, v) => opt.id === v.id}
          className="w-full md:w-[180px]"
          renderInput={(p) => <TextField {...p} label="Crop" />}
        />
        {varietyDisabled ? (
          <Tooltip title="Pick a crop first to enable Variety" arrow>
            <span className="block w-full md:inline-block md:w-auto">{variety}</span>
          </Tooltip>
        ) : (
          variety
        )}
        <Autocomplete
          size="small"
          options={apmcs}
          value={apmcValue}
          onChange={(_, opt) => setApmcId(opt ? Number(opt.id) : null)}
          getOptionLabel={(opt) => opt.name}
          isOptionEqualToValue={(opt, v) => opt.id === v.id}
          className="w-full md:w-[200px]"
          renderInput={(p) => <TextField {...p} label="APMC" />}
        />
        <TextField
          label="Metric"
          size="small"
          select
          value={metric}
          onChange={(e) => setMetric(e.target.value as '' | 'max' | 'modal')}
          className="w-full md:w-[120px]"
        >
          <MenuItem value="">All</MenuItem>
          {FORECAST_METRICS.map((m) => (
            <MenuItem key={m} value={m}>
              {m === 'max' ? 'Max' : 'Modal'}
            </MenuItem>
          ))}
        </TextField>
        <TextField
          label="Horizon"
          size="small"
          select
          value={horizon}
          onChange={(e) =>
            setHorizon(e.target.value === '' ? '' : Number(e.target.value))
          }
          className="w-full md:w-[120px]"
        >
          <MenuItem value="">All</MenuItem>
          {FORECAST_HORIZONS.map((h) => (
            <MenuItem key={h} value={h}>
              {h}d
            </MenuItem>
          ))}
        </TextField>
        <div className="flex items-end gap-2 md:contents">
          <TextField
            label="Page size"
            size="small"
            select
            value={pageSize}
            onChange={(e) => setPageSize(Number(e.target.value))}
            className="flex-1 md:w-[110px] md:flex-initial"
          >
            {[25, 50, 100].map((n) => (
              <MenuItem key={n} value={n}>
                {n}
              </MenuItem>
            ))}
          </TextField>

          <div className="flex items-center gap-2 md:ml-auto">
            {hasActiveFilters && (
              <Button variant="text" size="small" onClick={clearFilters}>
                Clear filters
              </Button>
            )}
            <Tooltip title={isFetching ? 'Loading…' : 'Refresh'}>
              <span>
                <IconButton onClick={() => refetch()} size="small" disabled={isFetching}>
                  {isFetching ? <CircularProgress size={16} thickness={5} /> : <MdRefresh />}
                </IconButton>
              </span>
            </Tooltip>
          </div>
        </div>
      </div>

      <div className="relative flex min-h-0 flex-1 overflow-hidden rounded-xl border border-border bg-surface">
        {isFetching && data && (
          <LinearProgress
            sx={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              zIndex: 5,
              height: 2,
            }}
          />
        )}
        {isInitialLoad ? (
          <GridSkeleton
            rows={6}
            columns={[
              { width: '1.5fr', bar: 'w-3/4' },
              { width: '110px', bar: 'w-12' },
              { width: '110px', bar: 'w-12' },
              { width: '150px', bar: 'w-20' },
              { width: '150px', bar: 'w-20' },
              { width: '1fr', bar: 'w-2/3' },
              { width: '110px', bar: 'w-14' },
              { width: '200px', bar: 'w-24' },
              { width: '120px', empty: true },
            ]}
          />
        ) : showEmpty ? (
          hasActiveFilters ? (
            <PageEmpty
              icon={MdFilterListOff}
              title="No matches"
              message="No forecasts match the current filters. Try widening crop / metric / horizon."
              action={
                <Button variant="outlined" size="small" onClick={clearFilters}>
                  Clear filters
                </Button>
              }
            />
          ) : (
            <PageEmpty
              icon={MdInsights}
              title="No forecasts yet"
              message="Forecasts appear here after the prediction pipeline runs. Make sure the relevant price-series rows have run_prediction_pipeline = true."
            />
          )
        ) : (
          <ForecastsGrid
            data={data}
            onEdit={setEditingRow}
            onChart={setChartingRow}
            onSortChange={setSort}
          />
        )}
      </div>

      <DataPagination
        page={page}
        pageSize={pageSize}
        totalRecords={totalRecords}
        onPageChange={setPage}
      />

      <ForecastEditDialog
        open={!!editingRow}
        row={editingRow}
        onClose={() => setEditingRow(null)}
      />
      <ForecastChartDialog
        open={!!chartingRow}
        row={chartingRow}
        onClose={() => setChartingRow(null)}
      />
    </Page>
  );
}
